import { SubjectSaveStatus } from "./SubjectSaveStatus";
import { toast } from "sonner";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ImagePlus, Loader2 } from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Text } from "@/components/ui/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { SubjectEditorProps } from "@/types/creative-library";
import type { CreativeSubjectReferenceView } from "@digital-twins/shared-types";
import { SubjectReferenceTips } from "./SubjectReferenceTips";
import { SubjectReferenceGallery } from "./SubjectReferenceGallery";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { ArchiveCreativeEntity } from "./ArchiveCreativeEntity";
import { CreativeUpload } from "./CreativeUpload";

export function SubjectEditor({
  subject,
  brand,
  creatives,
  onClose,
  onSaved,
  onUse,
  createOnly,
  onPersisted,
}: SubjectEditorProps) {
  const { t } = useTranslation("app");
  const [saved, setSaved] = useState(subject);
  const [name, setName] = useState(subject?.name ?? "");
  const [kind, setKind] = useState<"person" | "mascot">(
    subject?.kind ?? "person",
  );
  const [guidance, setGuidance] = useState(subject?.guidance ?? "");
  const [references, setReferences] = useState(subject?.referenceTokens ?? []);
  const [primary, setPrimary] = useState(
    subject?.primaryReferenceToken ?? null,
  );
  const [views, setViews] = useState<
    Record<string, CreativeSubjectReferenceView>
  >(subject?.referenceViews ?? {});
  const [upload, setUpload] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<typeof creatives>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const canEdit = Boolean(brand?.canEdit && (!saved || saved.canEdit));
  const oldReferences = saved?.referenceTokens ?? [];
  const refsDirty =
    references.length !== oldReferences.length ||
    references.some((token) => !oldReferences.includes(token));
  const primaryDirty = primary !== (saved?.primaryReferenceToken ?? null);
  const savedViews = saved?.referenceViews ?? {};
  const viewsDirty =
    Object.keys(views).length !== Object.keys(savedViews).length ||
    Object.entries(views).some(([token, view]) => savedViews[token] !== view);
  const dirty =
    !saved ||
    name !== saved.name ||
    kind !== saved.kind ||
    guidance !== saved.guidance ||
    refsDirty ||
    primaryDirty ||
    viewsDirty;
  const photos = [
    ...creatives,
    ...uploadedPhotos.filter(
      (photo) => !creatives.some((item) => item.token === photo.token),
    ),
  ].filter(
    (item) =>
      item.brandToken === brand?.token &&
      item.role === "identity" &&
      (item.origin !== "generated" ||
        (saved &&
          item.generationProvenance?.provenanceState === "known" &&
          item.generationProvenance.subject?.token === saved.token)),
  );
  const missing = references.some(
    (token) => !photos.some((photo) => photo.token === token),
  );
  const canUse = Boolean(
    saved &&
    !dirty &&
    references.some((token) => photos.some((photo) => photo.token === token)),
  );
  async function save() {
    if (!brand || !canEdit || !name.trim() || saving) return;
    setSaving(true);
    setError(false);
    try {
      const details = { name: name.trim(), kind, guidance };
      const updated = saved
        ? await creativeLibraryApi.updateSubject(saved.token, {
            ...details,
            revision: saved.revision,
            ...(refsDirty ? { referenceTokens: references } : {}),
            ...(primaryDirty ? { primaryReferenceToken: primary } : {}),
            ...(viewsDirty ? { referenceViews: views } : {}),
          })
        : await creativeLibraryApi.createSubject({
            ...details,
            brandToken: brand.token,
            referenceTokens: references,
            primaryReferenceToken: primary,
            referenceViews: views,
          });
      toast.success(t("creativeLibrary.identitySaved"));
      setSaved(updated);
      setName(updated.name);
      setKind(updated.kind);
      setGuidance(updated.guidance);
      setReferences(updated.referenceTokens);
      setPrimary(updated.primaryReferenceToken ?? null);
      setViews(updated.referenceViews ?? {});
      onSaved();
      onPersisted?.(updated);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  async function reload() {
    if (!saved) return;
    setSaving(true);
    setError(false);
    try {
      const updated = await creativeLibraryApi.subject(saved.token);
      setSaved(updated);
      setName(updated.name);
      setKind(updated.kind);
      setGuidance(updated.guidance);
      setReferences(updated.referenceTokens);
      setPrimary(updated.primaryReferenceToken ?? null);
      setViews(updated.referenceViews ?? {});
      onSaved();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  function changeReferences(tokens: string[]) {
    setReferences(tokens);
    if (primary && !tokens.includes(primary)) setPrimary(null);
    setViews(
      Object.fromEntries(
        Object.entries(views).filter(([token]) => tokens.includes(token)),
      ),
    );
  }
  return (
    <>
      <CreativeEditorDialog
        open={Boolean(brand)}
        busy={saving || upload}
        onClose={onClose}
        kind="subjects"
        title={saved?.name ?? t("creativeLibrary.newSubject")}
        description={brand?.name ?? ""}
        testId="creative-subject-editor"
        footer={
          <>
            {saved && !error && <SubjectSaveStatus dirty={dirty} />}
            {saved && canEdit && (
              <ArchiveCreativeEntity
                kind="subjects"
                token={saved.token}
                disabled={saving}
                onArchived={() => {
                  onSaved();
                  onClose();
                }}
              />
            )}
            <Button
              variant="outline"
              disabled={saving || upload}
              onClick={onClose}
            >
              {t("creativeLibrary.done")}
            </Button>
            {canEdit && (
              <Button
                variant={createOnly ? "primary" : "outline"}
                disabled={
                  !dirty ||
                  !name.trim() ||
                  saving ||
                  upload ||
                  (refsDirty && missing)
                }
                onClick={() => void save()}
                data-testid="subject-save"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {t(
                  saved && !dirty && !saving
                    ? "creativeLibrary.identitySaved"
                    : "creativeLibrary.saveSubject",
                )}
              </Button>
            )}
            {!createOnly && (
              <Button
                variant="primary"
                disabled={!canUse || saving || upload}
                onClick={() => {
                  if (saved && canUse) onUse(saved);
                }}
                data-testid="subject-create"
              >
                {t("creativeLibrary.createWithSubject")}
              </Button>
            )}
          </>
        }
      >
        <CreativeEditorSection>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="subject-name">
                {t("creativeLibrary.subjectName")}
              </Label>
              <Input
                id="subject-name"
                value={name}
                maxLength={255}
                disabled={!canEdit || saving}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t("creativeLibrary.subjectKind")}</Label>
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as "person" | "mascot")}
                disabled={!canEdit || saving}
              >
                <SelectTrigger aria-label={t("creativeLibrary.subjectKind")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">
                    {t("creativeLibrary.subjectKinds.person")}
                  </SelectItem>
                  <SelectItem value="mascot">
                    {t("creativeLibrary.subjectKinds.mascot")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Label htmlFor="subject-guidance">
            {t("creativeLibrary.subjectGuidance")}
          </Label>
          <Textarea
            id="subject-guidance"
            value={guidance}
            maxLength={1500}
            rows={3}
            disabled={!canEdit || saving}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder={t("creativeLibrary.subjectGuidanceHint")}
          />
        </CreativeEditorSection>
        {!createOnly && (
          <CreativeEditorSection>
            <Text size="sm">{t("creativeLibrary.subjectGallery")}</Text>
            <SubjectReferenceTips kind={kind} />
            {canEdit && (
              <Button
                variant="outline"
                className="self-start gap-2"
                onClick={() => setUpload(true)}
                disabled={saving || upload}
              >
                <ImagePlus className="h-4 w-4" />
                {t("creativeLibrary.uploadIdentity")}
              </Button>
            )}
            {upload && (
              <CreativeUpload
                open={upload}
                embedded
                galleryUpload
                maxFiles={20 - references.length}
                onUploaded={(image) => {
                  setUploadedPhotos((previous) => [...previous, image]);
                  setReferences((previous) => [
                    ...new Set([...previous, image.token]),
                  ]);
                }}
                presetRole="identity"
                presetBrandToken={brand?.token}
                brands={brand ? [brand] : []}
                products={[]}
                onClose={() => setUpload(false)}
                onSaved={() => {
                  onSaved();
                  toast.info(t("creativeLibrary.photosDraftHint"));
                }}
              />
            )}
            {(saved?.unavailableReferenceCount ?? 0) > 0 && (
              <Text size="xs" color="muted" role="status">
                {t("creativeLibrary.subjectUnavailableReferences")}
              </Text>
            )}
            {missing && (
              <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
                <Text size="xs" color="muted" role="status">
                  {t("creativeLibrary.productReferenceUnavailable")}
                </Text>
                {saved && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={saving}
                    onClick={() => void reload()}
                  >
                    {t("creativeLibrary.reload")}
                  </Button>
                )}
                {canEdit &&
                  !saved?.unavailableReferenceCount &&
                  references
                    .filter(
                      (token) => !photos.some((photo) => photo.token === token),
                    )
                    .map((token) => (
                      <Button
                        key={token}
                        variant="ghost"
                        size="sm"
                        disabled={saving}
                        onClick={() =>
                          changeReferences(
                            references.filter((value) => value !== token),
                          )
                        }
                        data-testid={`subject-remove-unavailable-${token}`}
                      >
                        {t("creativeLibrary.remove")} ·{" "}
                        {t("creativeLibrary.unavailableSelection")}
                      </Button>
                    ))}
              </div>
            )}
            <SubjectReferenceGallery
              photos={photos}
              references={references}
              primary={primary}
              views={views}
              canEdit={canEdit}
              saving={saving}
              unavailable={(saved?.unavailableReferenceCount ?? 0) > 0}
              onReferencesChange={changeReferences}
              onPrimaryChange={setPrimary}
              onViewsChange={setViews}
            />
            {!photos.length && (
              <Text size="sm" color="muted">
                {t("creativeLibrary.noIdentityPhotos")}
              </Text>
            )}
            <Text size="xs" color="muted">
              {t(
                dirty
                  ? "creativeLibrary.saveIdentityBeforeCreate"
                  : "creativeLibrary.subjectCreationHint",
              )}
            </Text>
          </CreativeEditorSection>
        )}
        {error && (
          <div className="flex items-center justify-between gap-2">
            <Text
              size="sm"
              className="flex items-center gap-2 text-destructive"
              role="alert"
            >
              <AlertCircle className="h-4 w-4" />
              {t("creativeLibrary.subjectSaveFailed")}
            </Text>
            {saved && (
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => void reload()}
              >
                {t("creativeLibrary.reload")}
              </Button>
            )}
          </div>
        )}
      </CreativeEditorDialog>
    </>
  );
}
